import {
  GoalWithProgress,
  ProgressType,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../../../services/api.types'
import React from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { t } from 'ttag'
import { formatThousands } from '../../../../common/helpers/translations'
import { primaryBlue, darkBlue, lightBlue, lighterGreen } from '../../../../styles/material-ui-theme-v2'
import { openActivityIndexArticle } from '../../../../common/helpers/routeHelpers'
import YumGrid from '../../../YumGrid/YumGrid'
import {
  getChallengeDailyProgressCurrently,
  getChallengeGoalUnits,
  getChallengeProgressUnits,
  isActivityIndexType,
  isCustomType,
  isDistanceType,
} from '../../../../common/helpers/challenges'
import { getActivityType, getCustomCategory } from '../../../../services/CustomTypes'

import moment from 'moment/moment'
import { convertUTCToLocalDate, getShortDateString } from '../../../../common/helpers/date'
import { getFloorPercentage, getRoundPercentage } from '../../../YumManualEntryForm/YumManualEntryForm'
import { isRtl } from '../../../../i18nInit'
import { YumActivityIcon } from '../../../YumActivityIcon/YumActivityIcon'

export interface IYumChallengeActivityIndexProgressProps {
  goal: GoalWithProgress
  dailyDiary: UserDailyDiaryProgress[]
  progressResults: UserDailyProgress[]
  customProgress: UserCustomDailyProgress[]
}

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    padding: '10px',
    borderRadius: '8px',
    color: darkBlue,
    marginBottom: '16px',
  },
  header: {
    display: 'flex',
    //gap: 10,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    marginBottom: '32px',
    userSelect: 'none',
  },
  headerIcon: {
    flexShrink: 0,
    marginRight: isRtl ? 0 : '10px',
    marginLeft: isRtl ? '10px' : 0,
  },
  totalGoalLabel: {
    fontWeight: 500,
    fontSize: 12,
    lineHeight: '14px',
  },
  totalGoalText: {
    fontWeight: 500,
    fontSize: 15,
    lineHeight: '18px',
  },

  progressCurrently: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
  },
  progressGoal: {
    fontWeight: 500,
    fontSize: 28,
    lineHeight: '33px',
    opacity: 0.8,
  },
  progressUnits: {
    fontWeight: 500,
    fontSize: 18,
    lineHeight: '21px',
    opacity: 0.8,
  },

  mainProgressBar: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    background: 'rgba(255, 255, 255, 0.4)',
  },
  mainProgressBarInner: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    background: darkBlue,
    transition: 'all 0.2s ease-out',
  },
  wrapper: {
    backgroundColor: 'white',
    padding: '10px',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  total: {
    fontWeight: 500,
    fontSize: '28px',
    lineHeight: '33px',
  },
  unit: {
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '21px',
    opacity: 0.5,
    marginLeft: '4px',
  },
  progressBar: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    background: 'rgba(36, 40, 78, 0.1)',
  },
  progressBarInner: {
    borderRadius: '10px',
    height: '8px',
    marginTop: '8px',
    transition: 'all 0.2s ease-out',
  },
  linkWrapper: { textAlign: 'center', marginBottom: '16px' },
  link: {
    color: primaryBlue,
    fontSize: '13px',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:focus': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:active': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'underline !important',
      },
    },
  },
}))

export default function YumChallengeDailyProgress(props: IYumChallengeActivityIndexProgressProps) {
  const classes = useStyles()
  const { goal, dailyDiary, progressResults, customProgress } = props
  const { progressType, customType, dailyPersistenceGoal, endDate, manualEntryEnabled } = goal
  // const isCustom = isCustomType(progressType)
  const isDistance = isDistanceType(customType)
  const isActivityIndex = isActivityIndexType(progressType)

  const challengeEndDate = moment(convertUTCToLocalDate(new Date(endDate)))
  const challengeHasEnded = challengeEndDate.isBefore(new Date())
  const momentDate = challengeHasEnded ? challengeEndDate : moment()
  const dayString = momentDate.clone().locale('en').format('YYYY-MM-DD')

  const showDigits = isDistance ? 2 : undefined
  const dailyProgress = getChallengeDailyProgressCurrently(
    progressType,
    dailyDiary,
    progressResults,
    customProgress,
    dayString,
    manualEntryEnabled,
  )
  const progressCurrently = formatThousands(dailyProgress, showDigits)
  const progressGoal = formatThousands(dailyPersistenceGoal || 0)
  const progressUnits = getChallengeGoalUnits(goal)
  const progressPercentage = !dailyPersistenceGoal ? 0 : getFloorPercentage(dailyProgress / dailyPersistenceGoal)
  /*: isDistance
    ? getFloorPercentage(dailyProgress / dailyPersistenceGoal)
    : getRoundPercentage(dailyProgress / dailyPersistenceGoal)*/

  const MainComponent = ({
    icon,
    achievedIcon,
    dailyPersistenceGoal,
    percentage,
    progressCurrently,
    progressUnits,
    progressGoal,
  }: {
    icon: any
    achievedIcon: any
    dailyPersistenceGoal: number | null
    percentage: number
    progressCurrently: string
    progressUnits: string
    progressGoal?: string
  }) => {
    const achieved = percentage >= 100
    return (
      <div
        className={classes.mainWrapper}
        style={{
          backgroundColor: `${achieved ? lighterGreen : lightBlue}`,
        }}
      >
        <div className={classes.header}>
          <div className={classes.headerIcon}>{achieved ? achievedIcon : icon}</div>
          <YumGrid>
            <div>
              <>
                <div className={classes.totalGoalLabel}>
                  {t`Daily goal`}
                  {challengeHasEnded && ` (${getShortDateString(momentDate.valueOf(), true)})`}
                </div>
                <div className={classes.totalGoalText}>
                  {getActivityType(goal)}
                  {progressType === ProgressType.PERSISTENCE_ALL && dailyPersistenceGoal
                    ? ` ${dailyPersistenceGoal}%`
                    : ''}
                </div>
              </>
            </div>
          </YumGrid>
        </div>
        <YumGrid>
          <div>
            <span className={classes.progressCurrently}>{progressCurrently}</span>
            {isActivityIndex ? (
              <>
                <span className={classes.progressUnits}> {progressUnits}</span>
              </>
            ) : (
              <>
                <span className={classes.progressGoal}> / {progressGoal}</span>{' '}
                <span className={classes.progressUnits}>{progressUnits}</span>
              </>
            )}
          </div>
          {!isActivityIndex && (
            <div style={{ whiteSpace: 'nowrap' }}>
              <span className={classes.progressCurrently}>{percentage}</span>
              <span className={classes.progressUnits}> %</span>
            </div>
          )}
        </YumGrid>
        <div className={classes.mainProgressBar}>
          <div
            className={classes.mainProgressBarInner}
            style={{
              width: `${achieved ? '100' : percentage}%`,
            }}
          />
        </div>
      </div>
    )
  }

  const category = getCustomCategory(progressType, customType)
  return (
    <>
      <MainComponent
        icon={<YumActivityIcon category={category} color={'darkBlue'} bg={'white'} />}
        achievedIcon={<YumActivityIcon category={category} color={'green'} bg={'white'} />}
        dailyPersistenceGoal={dailyPersistenceGoal}
        percentage={progressPercentage}
        progressCurrently={progressCurrently}
        progressUnits={progressUnits}
        progressGoal={progressGoal}
      />
      {isActivityIndex && (
        <div className={classes.linkWrapper}>
          <a
            className={classes.link}
            href={undefined}
            onClick={openActivityIndexArticle}
          >{t`What's activity index?`}</a>
        </div>
      )}
    </>
  )
}
